import { useTranslation } from "next-i18next";
import {
  Form,
  DatePicker,
  SubStepButtons,
  PhoneInput,
} from "@talladega/components";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { useRouter } from "next/router";
import { LocalDate } from "@js-joda/core";
import { customerValidationDeprecated as customerValidation } from "@talladega/helpers";
import { usePhoneNumberMask } from "@talladega/hooks";
import {
  FinancingPreQualificationMode,
  introductionMode,
  residenceinfoMode,
} from "../types/FinancingPreQualificationMode";
import { PreQualificationFormProps } from "../types/FinancingPreQualificationFormProps";

type Props = {
  onChangeMode(newMode: FinancingPreQualificationMode): void;
};

const FinancingPreQualificationPersonalInfo = ({ onChangeMode }: Props) => {
  const { t } = useTranslation(["common"]);
  const { locale } = useRouter();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<PreQualificationFormProps>();

  const { unMaskPipe: phoneUnMaskPipe } = usePhoneNumberMask();

  const onFormSubmit: SubmitHandler<PreQualificationFormProps> = () => {
    onChangeMode(residenceinfoMode);
  };

  const onBack: () => void = () => {
    onChangeMode(introductionMode);
  };

  return (
    <div className="w-full mb-5">
      <div>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="flex flex-col gap-4">
            <div>
              <Form.Input
                label={t("common:first_name")}
                maxLength={customerValidation.firstName.maxLength}
                feedback={
                  errors.firstName && (
                    <p className="text-error-500">{errors.firstName.message}</p>
                  )
                }
                {...register("firstName", {
                  required: { value: true, message: t("common:required") },
                  validate: customerValidation.firstName.valid,
                  maxLength: customerValidation.firstName.maxLength,
                })}
              />
              <div className="text-sm text-secondary">
                {t("common:enter_name_as_displayed_on_license")}
              </div>
            </div>
            <Form.Input
              label={t("common:last_name")}
              maxLength={customerValidation.lastName.maxLength}
              feedback={
                errors.lastName && (
                  <p className="text-error-500">{errors.lastName.message}</p>
                )
              }
              {...register("lastName", {
                required: { value: true, message: t("common:required") },
                validate: customerValidation.lastName.valid,
                maxLength: customerValidation.lastName.maxLength,
              })}
            />

            <Form.Input
              label={t("common:email")}
              maxLength={customerValidation.email.maxLength}
              feedback={
                errors.email && (
                  <p className="text-error-500">{errors.email.message}</p>
                )
              }
              {...register("email", {
                required: { value: true, message: t("common:required") },
                pattern: customerValidation.email.pattern,
                maxLength: customerValidation.email.maxLength,
              })}
            />
            <div className="flex flex-row gap-4">
              <PhoneInput
                label={t("common:phone")}
                feedback={errors.phoneNumber?.message}
                status={errors.phoneNumber ? "error" : "default"}
                {...register("phoneNumber", {
                  required: { value: true, message: t("common:required") },
                  validate: {
                    minLength: (value) =>
                      customerValidation.phoneNumber.valid(
                        phoneUnMaskPipe(value)
                      ) || t("common:phone_min_length"),
                    validateAreaCode: (value) =>
                      customerValidation.phoneNumber.validAreaCode(
                        phoneUnMaskPipe(value)
                      ) || t("common:form_error_invalid_phone_number"),
                  },
                })}
              />

              <Controller
                name="dateOfBirth"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("common:required"),
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  const { onChange, value, ...fieldProps } = field;
                  return (
                    <DatePicker
                      key="c2c-pre-qual-birthdate"
                      selected={value}
                      label={t("common:date_of_birth")}
                      popperPlacement="top-start"
                      onChange={onChange}
                      minDate={customerValidation.dateOfBirth.getMinDate(
                        LocalDate.now()
                      )}
                      maxDate={customerValidation.dateOfBirth.getMaxDate(
                        LocalDate.now()
                      )}
                      locale={locale}
                      status={error ? "error" : "default"}
                      feedback={error ? error.message : ""}
                      suppressHotjar
                      {...fieldProps}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="mt-6">
            <SubStepButtons
              backButton={{ onClick: onBack }}
              alignment="justify-between"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancingPreQualificationPersonalInfo;
